import { CaretLeft } from '@liveauctioneers/hammer-ui-core/icons';
import { FormattedMessage } from '@liveauctioneers/hammer-ui-core/intl';
import { BodyPrimary } from '@liveauctioneers/hammer-ui-core/text';
import * as React from 'react';
import styled from 'styled-components';

import { SubtleMonochromeRouteLink } from '@/components/RouteLinks/RouteLinks';
import { getTaxCertManagementUrl } from '@/utils/urls';

import PageTitle from '../PageTitle/PageTitle';

type Props = {
    certCaptureUrl: string;
    certificateError: string | boolean;
};

const CertCaptureForm = ({ certCaptureUrl, certificateError }: Props) => {
    //a small timer to prevent the error message from flashing before the token is fetched
    const [loading, setLoading] = React.useState<boolean>(true);

    React.useEffect(() => {
        const timeout = setTimeout(() => {
            setLoading(false);
        }, 2000);

        return () => clearTimeout(timeout);
    }, []);

    const taxManagementUrl = getTaxCertManagementUrl();
    return (
        <StyledTaxCertCaptureForm>
            <StyledFormContainer>
                <Back>
                    <CaretLeft
                        className="inline-block"
                        size="xs"
                        style="bold"
                    />
                    <BackLinkWrapper>
                        <SubtleMonochromeRouteLink
                            className="inline-block h-full w-full"
                            to={taxManagementUrl}
                        >
                            <FormattedMessage id="certManagement.backToTaxManagement" />
                        </SubtleMonochromeRouteLink>
                    </BackLinkWrapper>
                </Back>
                <PageTitle title={<FormattedMessage id="certManagement.uploadYourTaxInfo" />} />
                {Boolean(certificateError) && !loading ? (
                    <BodyPrimary color="critical">
                        <FormattedMessage id="certManagement.missingCertToken" />
                    </BodyPrimary>
                ) : (
                    <StyledFrame src={certCaptureUrl} />
                )}
            </StyledFormContainer>
        </StyledTaxCertCaptureForm>
    );
};

export default React.memo<Props>(CertCaptureForm);

const StyledTaxCertCaptureForm = styled.div`
    margin: 0 auto;
    max-width: ${({ theme }) => theme.breakpoints.pageWidth};
`;

const StyledFormContainer = styled.div`
    align-self: center;
    width: calc(100% - 40px);
    max-width: 1200px;
    margin: 0 20px 20px;
`;

const StyledFrame = styled.iframe`
    width: 100%;
    min-height: 800px;
    border: none;

    @media (max-width: ${({ theme }) => theme.breakpoints.mobileWidth}) {
        width: 100%;
        min-height: 820px;
    }
`;

const Back = styled.div`
    margin: 19px 0 0;
`;

const BackLinkWrapper = styled.div`
    margin: 0 12px 0 8px;
    font-size: 13px;
    font-family: ${({ theme }) => theme.typography.fontPrimaryStrong};
    line-height: 16px;
    display: inline-block;
    color: ${({ theme }) => theme.colors.grey100};
    border-bottom: 1px solid ${({ theme }) => theme.typography.fontPrimary};
    transition:
        border-bottom 0.3s,
        color 0.3s;

    &:hover {
        color: ${({ theme }) => theme.colors.blue200};
        border-bottom: 1px solid ${({ theme }) => theme.colors.blue200};
    }
`;
